import {
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
  ReactElement,
  cloneElement,
  isValidElement,
  Children,
} from 'react';
import Box from '@mui/material/Box';
import { BoxComponentProps } from '../../base/box/Box';

type AnimationType = 'fadeInUp' | 'fadeInDown' | 'fadeInLeft' | 'fadeInRight';

interface StaggerOnViewProps extends Omit<BoxComponentProps, 'children'> {
  staggerDelay?: number;
  builderMode?: boolean;
  previewMode?: boolean;
  globalStyles?: { animation?: { enabled?: boolean; type?: AnimationType } } | any;
  animateOnMount?: boolean;
  animateOnScroll?: boolean;
  enableExitAnimation?: boolean;
  playOnce?: boolean;
}

const StaggerOnView = ({
  staggerDelay = 0.2,
  builderMode,
  previewMode,
  globalStyles,
  animateOnMount = false,
  animateOnScroll = true,
  enableExitAnimation = true,
  playOnce = false,
  children,
  style: incomingStyle,
  ...boxProps
}: PropsWithChildren<StaggerOnViewProps>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const visibleRef = useRef<boolean>(false);
  const [isExiting, setIsExiting] = useState(false);
  const exitingRef = useRef<boolean>(false);
  const hasPlayedRef = useRef<boolean>(false);
  const [hasClientRendered, setHasClientRendered] = useState(false);

  // Compute enabled if not explicitly provided
  const animationEnabledFromGlobal = globalStyles?.animation?.enabled ?? false;
  const computedEnabled: boolean = (() => {
    if (previewMode === false && builderMode === false) return animationEnabledFromGlobal;
    if (previewMode === false) return false;
    if (builderMode) return Boolean(animationEnabledFromGlobal);
    return Boolean(animationEnabledFromGlobal);
  })();

  const resolvedType: AnimationType = (globalStyles?.animation?.type as AnimationType) || 'fadeInUp';

  // We only apply JS-driven stagger/animation styles once we know
  // we're on the client. With JS disabled, this never runs and
  // children render without opacity/transform overrides.
  useEffect(() => {
    setHasClientRendered(true);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Respect reduced motion preference
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!computedEnabled || prefersReducedMotion) {
      if (!visibleRef.current) {
        visibleRef.current = true;
        setIsVisible(true);
      }
      if (exitingRef.current) {
        exitingRef.current = false;
        setIsExiting(false);
      }
      return;
    }

    // If we only animate on mount, skip IO and show immediately
    if (!animateOnScroll && animateOnMount) {
      if (!visibleRef.current) {
        visibleRef.current = true;
        setIsVisible(true);
      }
      if (exitingRef.current) {
        exitingRef.current = false;
        setIsExiting(false);
      }
      return;
    }

    const builderScroller = document.querySelector('[data-builder-container]') as HTMLElement | null;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const ratio = entry.intersectionRatio;
          const elementHeight = entry.boundingClientRect.height;
          const viewportHeight =
            builderScroller?.clientHeight ?? (typeof window !== 'undefined' ? window.innerHeight : undefined) ?? 0;
          const isLargeElement = viewportHeight > 0 ? elementHeight > viewportHeight * 1.2 : false;
          const allowEarlyExit = !isLargeElement;
          const hasPlayed = hasPlayedRef.current;
          // Start exit early when <= 0.3 (about 70% exited)
          if (
            animateOnScroll &&
            enableExitAnimation &&
            allowEarlyExit &&
            (!playOnce || !hasPlayed) &&
            ratio > 0 &&
            ratio <= 0.3
          ) {
            if (!exitingRef.current) {
              exitingRef.current = true;
              setIsExiting(true);
            }
            if (visibleRef.current) {
              visibleRef.current = false;
              setIsVisible(false);
            }
            return;
          }

          if (entry.isIntersecting) {
            if (!visibleRef.current) {
              visibleRef.current = true;
              setIsVisible(true);
            }
            if (!hasPlayedRef.current) {
              hasPlayedRef.current = true;
            }
            if (exitingRef.current) {
              exitingRef.current = false;
              setIsExiting(false);
            }
            // If not animating on scroll, unobserve after first intersection (one-time)
            if (!animateOnScroll || (playOnce && hasPlayedRef.current)) observer.unobserve(entry.target);
          } else if (animateOnScroll && enableExitAnimation && (!playOnce || !hasPlayedRef.current)) {
            // Re-trigger on every view: reset when leaving viewport
            if (visibleRef.current) {
              visibleRef.current = false;
              setIsVisible(false);
            }
            if (!exitingRef.current) {
              exitingRef.current = true;
              setIsExiting(true);
            }
          }
        });
      },
      {
        root: builderScroller || null,
        threshold: [0, 0.3, 0.6, 1],
        rootMargin: '0px 0px -10% 0px',
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [computedEnabled, animateOnMount, animateOnScroll, enableExitAnimation, playOnce]);

  // Entrance offset (opposite of exit)
  const entranceTransformByType: Record<AnimationType, string> = {
    fadeInUp: 'translate3d(0,-100px,0)',
    fadeInDown: 'translate3d(0,100px,0)',
    fadeInLeft: 'translate3d(-100px,0,0)',
    fadeInRight: 'translate3d(100px,0,0)',
  };
  const exitTransformByType: Record<AnimationType, string> = {
    fadeInUp: 'translate3d(0,100px,0)',
    fadeInDown: 'translate3d(0,-100px,0)',
    fadeInLeft: 'translate3d(100px,0,0)',
    fadeInRight: 'translate3d(-100px,0,0)',
  };

  const entranceTransform = entranceTransformByType[resolvedType];
  const exitTransform = exitTransformByType[resolvedType];
  const transitionDuration = '0.5s';
  const transitionTimingFunction = 'cubic-bezier(0.750, -0.015, 0.565, 1.055)';
  const MAX_STAGGER_DELAY_STEPS = 8;

  const getStaggerDelay = (isShown: boolean, index: number, step: number) => {
    if (!isShown) return '0s';
    const clampedIndex = Math.min(index, step);
    const totalDelay = clampedIndex * staggerDelay;
    return `${totalDelay}s`;
  };

  // Helper function to recursively apply stagger to children
  const applyStaggerToChildren = (childrenToProcess: React.ReactNode, startIndex = 0): React.ReactNode => {
    const shouldAnimate = hasClientRendered && computedEnabled;
    if (!shouldAnimate) return childrenToProcess;

    const childrenArray = Children.toArray(childrenToProcess);
    let currentIndex = startIndex;

    return childrenArray.map((child) => {
      if (!isValidElement(child)) return child;

      const childChildren = child.props.children;
      const childChildrenArray = Children.toArray(childChildren);
      const hasMultipleChildren = childChildrenArray.length > 1;

      // If child has multiple children (like mapped items), recursively apply stagger to them
      if (hasMultipleChildren) {
        const staggeredChildren = childChildrenArray.map((grandChild: React.ReactNode, grandChildIndex) => {
          if (!isValidElement(grandChild)) return grandChild;

          const grandChildStyle = (grandChild.props.style || {}) as React.CSSProperties;
          const grandChildSx = grandChild.props.sx || {};

          const staggerIndex = currentIndex + grandChildIndex;
          return cloneElement(grandChild as ReactElement, {
            key: grandChild.key || grandChildIndex,
            style: {
              ...grandChildStyle,
              ['--i' as string]: staggerIndex,
            },
            sx: {
              ...(typeof grandChildSx === 'object' && grandChildSx !== null ? grandChildSx : {}),
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translate3d(0,0,0)' : isExiting ? exitTransform : entranceTransform,
              transitionProperty: 'opacity, transform',
              transitionDuration,
              transitionTimingFunction,
              transitionDelay: getStaggerDelay(isVisible, staggerIndex, MAX_STAGGER_DELAY_STEPS),
            },
          });
        });

        currentIndex += childChildrenArray.length;
        return cloneElement(child as ReactElement, {
          ...child.props,
          children: staggeredChildren,
        });
      }

      // Apply stagger to direct children
      const childStyle = (child.props.style || {}) as React.CSSProperties;
      const childSx = child.props.sx || {};

      const staggerIndex = currentIndex;
      const staggeredChild = cloneElement(child as ReactElement, {
        key: child.key || currentIndex,
        style: {
          ...childStyle,
          ['--i' as string]: staggerIndex,
        },
        sx: {
          ...(typeof childSx === 'object' && childSx !== null ? childSx : {}),
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translate3d(0,0,0)' : isExiting ? exitTransform : entranceTransform,
          transitionProperty: 'opacity, transform',
          transitionDuration,
          transitionTimingFunction,
          transitionDelay: getStaggerDelay(isVisible, staggerIndex, MAX_STAGGER_DELAY_STEPS),
        },
      });

      currentIndex += 1;
      return staggeredChild;
    });
  };

  const childrenWithStagger = applyStaggerToChildren(children);

  const baseContainerStyle: React.CSSProperties = { width: '100%' };
  const style: React.CSSProperties = {
    ...baseContainerStyle,
    ...(incomingStyle || {}),
  };

  const setContainerRef = (el: HTMLDivElement | null) => {
    containerRef.current = el;
  };

  // Avoid mixing our internal ref with any external ref passed in boxProps
  const { ref: _ignoredRef, ...restBoxProps } = boxProps as { ref?: unknown };

  return (
    <Box ref={setContainerRef} style={style} {...(restBoxProps as BoxComponentProps)}>
      {childrenWithStagger}
    </Box>
  );
};

export default StaggerOnView;
